import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useWbsDraft } from './hooks/useWbsDraft';
import { flattenTree } from './lib/treeFlat';
import { keyOf, type DraftItem, type KeyId } from './types.local';
import WbsToolbar from './components/WbsToolbar';
import WbsTreeTable from './components/WbsTreeTable';
import WbsDetailsPanel from './components/WbsDetailsPanel';
import WbsEmptyState from './components/WbsEmptyState';
import { useWbsKeyboard } from './hooks/useWbsKeyboard';
import {
    removeItemWithOp,
    updateItem,
    apiBootstrapLatestDrafts,
    apiDiscardAllDrafts,
    apiRebuildDraftHierarchy,
    apiSaveDraftBatch,
} from './api';
import { useColumnResize } from 'src/hooks/useColumnResize';
import { ArrowsUpDownIcon } from '@heroicons/react/20/solid';
import { useProjectAccessCtx } from 'src/features/projects/ProjectAccessContext';
import { hasPerm } from 'src/features/projects/hooks/useProjectAccess';
import { useNavigate } from 'react-router-dom';
import WbsImportModal from './components/WbsImportModal';
import type { WbsImportPreview } from './lib/excelImport';
import { useToast } from 'src/shared/ui/toast/useToast';
import { exportWbsRowsToExcel } from './lib/excelExport';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

export default function WbsEditorContainer({ projectId }: { projectId: string }) {
    const navigate = useNavigate();
    const toast = useToast();

    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    const [insertMode, setInsertMode] = useState<'append' | 'reassign'>('append');
    const [showHierarchyForDraft, setShowHierarchyForDraft] = useState(false);
    const [previewOn, setPreviewOn] = useState(false);
    const [scheduleMode, setScheduleMode] = useState(true);
    const [askedContinue, setAskedContinue] = useState(false);
    const [continueEditing, setContinueEditing] = useState(false);

    const {
        loading,
        items,
        selectedKey,
        setSelectedKey,
        patchLocal,
        createRoot,
        addSameLevel,
        addChild,
        moveSibling,
        // removeLocal,
        // saveReorder,
        saveSingleItem,
        saveContent,
        publish,
        refresh,
        reorderDirty,
        dirtyContent,
        // versions,
        // currentVersion,
        // activateVersion,
        hasDraft,
        // draftCount,
        latestPublishedVersion,
        // baselineNodeIds,
        previewCodeMap,
        changeKindMap,
        softDelete,
        undoDelete,
    } = useWbsDraft(projectId);

    // 퍼블리시 권한 체크
    const { access, isLoading: accessLoading } = useProjectAccessCtx();
    const canPublish = !accessLoading && hasPerm(access?.permissions, 'wbs.publish');
    const hasItems = (items.length ?? 0) > 0;
    const canClickPublish = canPublish && hasItems;

    // useEffect(() => {
    //     if (hasDraft && !askedContinue) {
    //         const ok = window.confirm('편집중인 초안이 있습니다. 계속 편집을 하겠습니까?');
    //         setContinueEditing(ok);
    //         setAskedContinue(true);
    //     }
    // }, [askedContinue, hasDraft]);

    const splitLeft = useColumnResize('wbsSplitLeft', 1200, 960, 1600);
    const RIGTH_BASE = 640;
    const RIGHT_MIN = 320;
    const RIGHT_MAX = 640;
    const splitRight = useColumnResize('wbsSplitRight', RIGTH_BASE, RIGHT_MIN, RIGHT_MAX, { edge: 'right' });

    const handleSelect = useCallback((k: KeyId) => setSelectedKey(k), [setSelectedKey]);

    const handleMoveUp = useCallback(() => {
        if (selectedKey) moveSibling(selectedKey, 'up');
    }, [selectedKey, moveSibling]);

    const handleMoveDown = useCallback(() => {
        if (selectedKey) moveSibling(selectedKey, 'down');
    }, [selectedKey, moveSibling]);

    const selectedItem: DraftItem | null = useMemo(
        () => (selectedKey ? (items.find((x) => keyOf(x) === selectedKey) ?? null) : null),
        [selectedKey, items],
    );

    const selectedKind = useMemo(
        () => (selectedKey ? (changeKindMap[selectedKey] ?? null) : null),
        [selectedKey, changeKindMap],
    );

    const selectedHasPublished = !!selectedItem?.publishedListVersion;
    const selectedIsDeleted = !!selectedItem?.isDeleted;

    useWbsKeyboard(!!selectedKey, {
        onMoveUp: handleMoveUp,
        onMoveDown: handleMoveDown,
    });

    const rows = useMemo(() => flattenTree(items, { rootParent: null, sort: true, baseDepth: 0 }), [items]);

    const handleOnlineNameChange = useCallback(
        async (row: DraftItem, nextName: string) => {
            const trimmed = nextName.trim();
            if (!trimmed || trimmed === row.name) {
                return;
            }

            try {
                // TODO: row.nodeId! 을 제대로 처리하자
                const res = await updateItem(projectId, row.nodeId!, { name: trimmed });
            } catch (e) {
                console.error(e);
            }
        },
        [projectId],
    );

    const handleAddSameLevel = useCallback(async () => {
        const newKey = await addSameLevel(insertMode);
        if (newKey) setSelectedKey(newKey);
    }, [addSameLevel, insertMode, setSelectedKey]);

    const handleAddChild = useCallback(async () => {
        const newKey = await addChild(insertMode);
        if (newKey) setSelectedKey(newKey);
    }, [addChild, insertMode, setSelectedKey]);

    const [busyKey, setBusyKey] = useState<KeyId | null>(null);
    const [importOpen, setImportOpen] = useState(false);
    const [importBusy, setImportBusy] = useState(false);

    const handleSoftDelete = useCallback(
        async (row: DraftItem) => {
            if (!row?.nodeId) return;
            // eslint-disable-next-line no-restricted-globals
            if (!confirm('이 항목을 삭제하시겠습니까? 다음 버전의 WbS List부터는 보이지 않게됩니다.')) return;
            try {
                setBusyKey(keyOf(row));
                await softDelete(row.nodeId);
            } catch (e) {
                console.error(e);
                toast.error('삭제 중 오류가 발생했습니다.', {
                    title: '삭제 실패',
                });
            } finally {
                setBusyKey(null);
            }
        },
        [softDelete, toast],
    );

    // [추가] 삭제 취소(soft delete 취소)
    const handleUndoDelete = useCallback(
        async (row: DraftItem) => {
            if (!row?.nodeId) return;
            try {
                setBusyKey(keyOf(row));
                await undoDelete(row.nodeId);
            } catch (e) {
                console.error(e);
                toast.error('삭제 취소 중 오류가 발생했습니다.', {
                    title: '처리 실패',
                });
            } finally {
                setBusyKey(null);
            }
        },
        [undoDelete, toast],
    );

    // Draft와 Published가 섞여있을 때 조건에 따라 삭제하는 함수
    const handleRemoveItem = useCallback(
        async (item: DraftItem, op: 'discard' | 'retire' | 'auto' = 'auto') => {
            const nodeId = item?.nodeId;
            if (!nodeId) return;

            // eslint-disable-next-line no-restricted-globals
            if (!confirm('추가(또는 수정)을 취소하시겠습니까?')) return;

            // 1) 삭제 전 '이전 포커스 대상' 계산
            const currentKey = selectedKey;
            const idx = rows.findIndex((r) => keyOf(r) === currentKey);
            let nextFocusKey: KeyId | null = null;
            if (idx > 0)
                nextFocusKey = keyOf(rows[idx - 1]); // 직전항목
            else if (rows.length > 1) nextFocusKey = keyOf(rows[idx + 1]); // 없으면 다음 항목

            try {
                const res = await removeItemWithOp(projectId, nodeId, op);
                if (res) {
                    refresh();
                    if (nextFocusKey) setSelectedKey(nextFocusKey);
                }
            } catch (e) {
                console.error(e);
            }
        },
        [projectId, refresh, rows, selectedKey, setSelectedKey],
    );

    // 초안(draft) 일괄생성 핸들러
    const onBootstrapLatest = useCallback(async () => {
        // 정책: 항상 최신 ㅣ기준
        // backend bootstrap은 isCurrent 기준이므로, 최신이 current가 아니면 먼저 최신으로 activate
        // if (latestPublishedVersion != null && currentVersion !== latestPublishedVersion) {
        //     await activateVersion(latestPublishedVersion);
        // }
        await apiBootstrapLatestDrafts(projectId);
        await refresh();
        setAskedContinue(true);
        setContinueEditing(true);
    }, [projectId, refresh]);

    // 초안(draft) 폐기 핸들러
    const onDiscardDraft = useCallback(async () => {
        const ok = window.confirm('초안은 완전히 삭제되어 복구할 수 없습니다. 정말 폐기하시겠습니까?');
        if (!ok) return;
        await apiDiscardAllDrafts(projectId);
        await refresh();
        setAskedContinue(false);
        setContinueEditing(false);
    }, [projectId, refresh]);

    // 엑셀 임포트 핸들러
    const handleImportExcel = useCallback(
        async (preview: WbsImportPreview) => {
            try {
                setImportBusy(true);

                await apiSaveDraftBatch(projectId, preview.items);
                await apiRebuildDraftHierarchy(projectId);
                await refresh();
                setAskedContinue(true);
                setContinueEditing(true);
                setImportOpen(false);
                toast.success(`WBS ${preview.items.length}건을 import했습니다.`, {
                    title: '가져오기 완료',
                });
            } catch (err: any) {
                console.error(err);
                toast.error(err?.response?.data?.error?.message ?? err?.message ?? 'Import 중 오류가 발생했습니다.', {
                    title: '가져오기 실패',
                });
            } finally {
                setImportBusy(false);
            }
        },
        [projectId, refresh, toast],
    );

    const handleExportExcel = useCallback(() => {
        try {
            exportWbsRowsToExcel({
                rows,
                previewOn,
                previewCodeMap,
                zone,
            });

            toast.success('WBS Excel export가 완료되었습니다.', {
                title: '내보내기 완료',
            });
        } catch (err: any) {
            console.error(err);
            toast.error(err?.message ?? 'Export 중 오류가 발생했습니다.', {
                title: '내보내기 실패',
            });
        }
    }, [rows, previewOn, previewCodeMap, zone, toast]);

    // siblings를 orderIndex를 기준으로 정렬
    const getFamilyOf = useCallback(
        (nodeId: string) => {
            const me = items.find((x) => x.nodeId === nodeId) ?? null;
            if (!me) return { parent: null, siblings: [] as DraftItem[] };

            const parentId = me.parentNodeId ?? null;

            const toOrder = (v: any) => {
                if (v === null || v === undefined) return Number.POSITIVE_INFINITY;
                const n = Number(v);
                return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
            };

            const siblings = items
                .filter((x) => (x.parentNodeId ?? null) === parentId)
                .slice() // ✅ 원본 보호
                .sort((a, b) => {
                    const ao = toOrder((a as any).orderIndex);
                    const bo = toOrder((b as any).orderIndex);
                    if (ao !== bo) return ao - bo;

                    // tie-breaker: code -> name -> nodeId (원하는 기준으로 조정 가능)
                    const ac = a.code ?? '';
                    const bc = b.code ?? '';
                    if (ac !== bc) return ac.localeCompare(bc);

                    const an = a.name ?? '';
                    const bn = b.name ?? '';
                    if (an !== bn) return an.localeCompare(bn);

                    return String(a.nodeId).localeCompare(String(b.nodeId));
                });

            const parent = parentId ? (items.find((x) => x.nodeId === parentId) ?? null) : null;
            return { parent, siblings };
        },
        [items],
    );

    const handlePublish = useCallback(async () => {
        const ok = window.confirm(
            [
                '현재 Draft를 새 WBS 버전으로 Publish(게시) 하시겠습니까?',
                '',
                '- 현재 Draft가 새 정식 WBS 버전으로 발행됩니다.',
                '- 발행된 버전은 앞으로 기준 버전으로 사용됩니다.',
                '- 이 작업은 되돌릴 수 없습니다.',
                '- 발행 후에는 작은 수정 내용이라도 새로운 버전으로 발행해야 합니다.',
                '- 내용을 한 번 더 확인하세요.',
            ].join('\n'),
        );

        if (!ok) return;

        try {
            const version = await publish();

            if (version != null) {
                toast.success(`WBS Version V${version} 이(가) 성공적으로 Publish 되었습니다.`);
                navigate(`/projects/${projectId}/wbs`);
            } else {
                toast.error('Publish에 실패했습니다.');
            }
        } catch (err: any) {
            toast.error('Publish 중 오류가 발생했습니다.');
        }
    }, [navigate, projectId, publish, toast]);

    // === ADDED: gate (published exists, no draft) ===
    if (latestPublishedVersion != null && !hasDraft) {
        const nextVersion = latestPublishedVersion + 1;
        return (
            <div className="p-6 space-y-3">
                <div className="text-lg font-semibold">최신 버전으로 새 버전 편집 시작</div>
                <div className="text-sm text-gray-600">
                    최신 발행 버전(V{latestPublishedVersion})을 기준으로 새로운 버전(V{nextVersion})의 초안을
                    생성합니다.
                </div>
                <div className="flex gap-2">
                    <button
                        className="px-4 py-2 rounded-2xl bg-orange-500 text-white hover:bg-orange-400"
                        onClick={onBootstrapLatest}
                        type="button"
                    >
                        최신 버전으로 새 버전 편집 시작
                    </button>
                    {/* '새로고침' 버튼 삭제하고, '취소' 버튼을 두고 누르면 WbsPage로 되돌아 가기 */}
                    <button
                        className="px-4 py-2 rounded-2xl border hover:bg-gray-50"
                        onClick={() => navigate(`/projects/${projectId}/wbs`)}
                    >
                        취소
                    </button>
                    {/* <button className="px-4 py-2 rounded-2xl border" onClick={() => refresh()} type="button">
                        새로고침
                    </button> */}
                </div>
            </div>
        );
    }

    // === ADDED: gate (draft exists, user declined continue) ===
    if (hasDraft && !continueEditing) {
        return (
            <div className="p-6 space-y-3">
                <div className="text-lg font-semibold">편집중인 초안이 있습니다</div>
                <div className="text-sm text-gray-600">계속 편집하거나, 편집중인 초안을 폐기(복구 불가)할 수 있습니다.</div>
                <div className="flex gap-2">
                    <button
                        className="px-4 py-2 rounded-2xl bg-orange-500 text-white"
                        onClick={() => {
                            setAskedContinue(true);
                            setContinueEditing(true);
                        }}
                        type="button"
                    >
                        계속 편집
                    </button>
                    <button
                        className="px-4 py-2 rounded-2xl border border-rose-200 text-rose-700"
                        onClick={onDiscardDraft}
                        type="button"
                    >
                        초안 폐기
                    </button>
                </div>
            </div>
        );
    }

    if (!loading && items.length === 0) {
        return (
            <div className="flex flex-col gap-3">
                <WbsToolbar
                    reorderDirty={reorderDirty}
                    dirtyContentCount={dirtyContent.size}
                    // onSaveReorder={saveReorder}
                    onSaveContent={saveContent}
                    onAddSameLevel={handleAddSameLevel}
                    onAddChild={handleAddChild}
                    onPublish={handlePublish}
                    onRefresh={refresh}
                    // onOpenImport={() => setImportOpen(true)}
                    onOpenExport={handleExportExcel}
                    // 추가
                    insertMode={insertMode}
                    onChangeInsertMode={setInsertMode}
                    // onRebuildHierarchy={() => setShowHierarchyForDraft(true)}
                    onRebuildHierarchy={async () => {
                        await apiRebuildDraftHierarchy(projectId);
                        await refresh(); // ✅ draft 재조회 -> DB에 저장된 code/level이 r.code/r.level로 즉시 표시됨
                    }}
                    // versions={versions}
                    // currentVersion={currentVersion}
                    // onActivateVersion={activateVersion} // 필요?
                    previewOn={previewOn} // 필요?
                    onTogglePreview={() => setPreviewOn((v) => !v)}
                    scheduleMode={scheduleMode}
                    onToggleScheduleMode={() => setScheduleMode((v) => !v)}
                    canPublish={canClickPublish}
                    showDiscardDraft={hasDraft}
                    onDiscardDraft={onDiscardDraft}
                    onExitEditor={() => navigate(`/projects/${projectId}/wbs`)}
                />
                <div className="h-[70vh] bg-white border rounded-xl overflow-hidden">
                    <WbsEmptyState
                        onCreateRoot={createRoot}
                        onImport={() => setImportOpen(true)}
                        onTemplate={() =>
                            toast.info('템플릿으로 시작 기능은 준비 중입니다.', {
                                title: '준비 중',
                            })
                        }
                    />
                </div>

                <WbsImportModal
                    open={importOpen}
                    busy={importBusy}
                    onClose={() => {
                        if (importBusy) return;
                        setImportOpen(false);
                    }}
                    onConfirm={handleImportExcel}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3 h-[85vh] min-h-0">
            <WbsToolbar
                reorderDirty={reorderDirty}
                dirtyContentCount={dirtyContent.size}
                // onSaveReorder={saveReorder}
                onSaveContent={saveContent}
                onAddSameLevel={handleAddSameLevel}
                onAddChild={handleAddChild}
                onPublish={handlePublish}
                onRefresh={refresh}
                // onOpenImport={() => setImportOpen(true)}
                onOpenExport={handleExportExcel}
                // 추가
                insertMode={insertMode}
                onChangeInsertMode={setInsertMode}
                // onRebuildHierarchy={() => setShowHierarchyForDraft(true)}
                onRebuildHierarchy={async () => {
                    await apiRebuildDraftHierarchy(projectId);
                    await refresh(); // ✅ draft 재조회 -> DB에 저장된 code/level이 r.code/r.level로 즉시 표시됨
                }}
                // versions={versions}
                // currentVersion={currentVersion}
                // onActivateVersion={activateVersion}
                previewOn={previewOn}
                onTogglePreview={() => setPreviewOn((v) => !v)}
                scheduleMode={scheduleMode}
                onToggleScheduleMode={() => setScheduleMode((v) => !v)}
                canPublish={canClickPublish}
                showDiscardDraft={hasDraft}
                onDiscardDraft={onDiscardDraft}
                onExitEditor={() => navigate(`/projects/${projectId}/wbs`)}
            />

            <div className="flex gap-3 flex-1 min-h-0">
                {/* Left: Tree */}
                <div className="flex-1 min-w-0 bg-white border rounded-xl p-2 overflow-hidden flex flex-col">
                    <div className="flex-1 min-h-0">
                        {loading ? (
                            <div className="p-4 text-sm text-gray-500">Loading…</div>
                        ) : (
                            <WbsTreeTable
                                projectId={projectId}
                                rows={rows}
                                selectedKey={selectedKey}
                                onSelect={handleSelect}
                                onMoveUp={handleMoveUp}
                                onMoveDown={handleMoveDown}
                                onOnlineNameChange={handleOnlineNameChange}
                                onChange={(nodeId, patch) => patchLocal(nodeId, patch)}
                                changeKindMap={changeKindMap}
                                showHierarchyForDraft={showHierarchyForDraft}
                                onUndoDelete={handleUndoDelete}
                                previewOn={previewOn}
                                previewCodeMap={previewCodeMap}
                                onSoftDelete={handleSoftDelete}
                                busyKey={busyKey}
                                scheduleMode={scheduleMode}
                            />
                        )}
                    </div>
                    <div className="text-xs text-gray-500 mt-4 shrink-0">
                        <ArrowsUpDownIcon
                            className="inline-block h-4 w-4 mr-1 text-gray-400 align-[-2px]"
                            aria-hidden="true"
                        />
                        순서를 변경하고자 하는 항목을 선택하신 후 ALT + ↑/↓ 로 형제 순서 변경 후 “계층코드 재생성”
                        버튼을 누르면 계층코드가 자동으로 생성됩니다.
                    </div>
                </div>

                {/* Vertical Resizer */}
                <div
                    onMouseDown={splitRight.onMouseDown}
                    className="w-1 h-12 self-center cursor-col-resize bg-gray-200 hover:bg-gray-300 rounded-full"
                    style={{ marginLeft: -2, marginRight: -2 }}
                    title="패널 너비 조절"
                />

                {/* Right: Detail */}
                <div
                    className="bg-white border rounded-xl p-3 overflow-hidden flex flex-col min-h-0"
                    style={{ width: splitRight.width, minWidth: RIGHT_MIN }}
                >
                    <div className="flex-1 min-h-0 overflow-auto">
                        <WbsDetailsPanel
                            item={selectedItem}
                            status={selectedKind}
                            hasPublished={selectedHasPublished}
                            isDeleted={selectedIsDeleted}
                            onChange={(nodeId, patch) => patchLocal(nodeId, patch)}
                            onAddSibling={handleAddSameLevel}
                            onAddChild={handleAddChild}
                            onSaveContent={saveContent}
                            onSaveSingleItem={saveSingleItem}
                            onRemove={handleRemoveItem} // op='discard' | 'retire' | 'auto'
                            onSoftDelete={handleSoftDelete} // 소프트 삭제
                            onUndoDelete={handleUndoDelete} // 삭제 취소
                            getFamilyOf={getFamilyOf}
                        />
                    </div>
                </div>
            </div>

            {/* (선택) 변경 내역 패널 – 필요 시 연결 */}
            {/* <ChangesDrawer changes={[]} /> */}

            <WbsImportModal
                open={importOpen}
                busy={importBusy}
                onClose={() => {
                    if (importBusy) return;
                    setImportOpen(false);
                }}
                onConfirm={handleImportExcel}
            />
        </div>
    );
}
